import React from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ServiceMemberDto } from "../../pages/types/types";
import ServiceEnrollmentMembershipCard from "../../pages/features/manage-party/components/ServiceEnrollmentMembershipCard";
import ServiceProfileExtensionRenderer from "./ServiceProfileExtensionRenderer";

interface ServiceMemberListProps {
  members: ServiceMemberDto[];
  expandedPartyId: number | null;
  onOpenProfile: (member: ServiceMemberDto) => void;
  onCreateProfileUpdate: (member: ServiceMemberDto) => void;
  onToggleExpanded: (partyId: number) => void;
  onMembershipChanged?: () => void | Promise<void>;
  creatingProfileUpdatePartyId?: number | null;
}

const memberTypeLabel = (memberType?: string) => {
  switch (memberType) {
    case "PrimaryContact":
      return "Primary contact";
    case "Volunteer":
      return "Volunteer";
    case "Staff":
      return "Staff";
    default:
      return "Service user";
  }
};

const memberTypesLabel = (member: ServiceMemberDto) =>
  (member.memberTypes?.length ? member.memberTypes : [member.memberType])
    .map(memberTypeLabel)
    .join(", ");

export default function ServiceMemberList({
  members, expandedPartyId, onOpenProfile, onCreateProfileUpdate,
  onToggleExpanded, onMembershipChanged, creatingProfileUpdatePartyId = null,
}: ServiceMemberListProps) {
  if (!members.length) return <div className="card"><p className="muted">No service members match your search.</p></div>;

  return (
    <div className="service-member-card-list">
      {members.map((member) => {
        const expanded = expandedPartyId === member.partyId;
        return (
          <article className={`service-member-card ${expanded ? "expanded" : ""}`} key={member.serviceEnrollmentId}>
            <button type="button" className="service-member-card-summary" onClick={() => onToggleExpanded(member.partyId)} aria-label={`${expanded ? "Hide" : "Show"} details for ${member.displayName}`} aria-expanded={expanded}>
              <span className="service-member-name">{member.displayName}</span>
              <span className="service-member-type">{memberTypesLabel(member)}</span>
              <span className="service-member-card-chevron" aria-hidden="true">{expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</span>
            </button>
            {expanded && (
              <div className="service-member-detail-panel">
                <div className="service-member-expanded-actions">
                  <button type="button" onClick={() => onOpenProfile(member)}><VisibilityIcon fontSize="small" /> Open profile</button>
                  <button type="button" onClick={() => onCreateProfileUpdate(member)} disabled={creatingProfileUpdatePartyId === member.partyId}><ManageAccountsIcon fontSize="small" /> Create update link</button>
                </div>
                <ServiceProfileExtensionRenderer member={member} />
                <ServiceEnrollmentMembershipCard
                  serviceEnrollmentId={member.serviceEnrollmentId}
                  serviceId={member.serviceId}
                  memberTypes={
                    member.memberTypes?.length
                      ? member.memberTypes
                      : [member.memberType]
                  }
                  onSaved={onMembershipChanged}
                />
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
